import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function AuthPage() {
  const location = useLocation();
  const modeParam = new URLSearchParams(location.search).get('mode');
  const initialIsLogin = !(location.pathname === '/signup' || modeParam === 'signup');
  const [isLogin, setIsLogin] = useState(initialIsLogin);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
  });
  const [errors, setErrors] = useState({});

  const { login, register } = useAuth();
  const navigate = useNavigate();
  const redirect = new URLSearchParams(location.search).get('redirect') || '/';

  useEffect(() => {
    const mode = new URLSearchParams(location.search).get('mode');
    const shouldLogin = !(location.pathname === '/signup' || mode === 'signup');
    setIsLogin(shouldLogin);
  }, [location.pathname, location.search]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
    
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    
    if (!isLogin) {
      if (!formData.firstName) newErrors.firstName = 'First name is required';
      if (!formData.lastName) newErrors.lastName = 'Last name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors below');
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back! 🎉');
      } else {
        await register(formData.email, formData.password, formData.firstName, formData.lastName);
        toast.success('Account created successfully! Welcome to Belt & Tingz 🎉');
      }
      navigate(redirect);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Authentication failed';
      toast.error(errorMsg);
      if (errorMsg.toLowerCase().includes('already')) {
        setErrors({ email: 'Email already registered. Please login instead.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="auth-page">
      {/* Left Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1620109176813-e91290f6c795?w=1200&q=80"
          alt="Leather craftsmanship"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-leather-900/40"></div>
        <div className="absolute bottom-16 left-16 right-16">
          <h2 className="font-heading text-4xl text-white mb-4">Experience Luxury</h2>
          <p className="text-cream-100/80">
            Join Belt & Tingz for exclusive access to new collections, member-only offers, and personalized recommendations.
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="logo-text text-xl text-leather-900 block mb-12">
            BELT & TINGZ
          </Link>

          <h1 className="font-heading text-3xl text-leather-900 mb-2">
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </h1>
          <p className="text-leather-600 mb-8">
            {isLogin ? 'Sign in to continue to your account' : 'Join us for an exclusive experience'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs tracking-widest uppercase text-leather-600 block mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className={`input-flushed w-full ${errors.firstName ? 'border-red-500 bg-red-50' : ''}`}
                      required={!isLogin}
                      data-testid="first-name-input"
                    />
                    {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
                  </div>
                  <div>
                    <label className="text-xs tracking-widest uppercase text-leather-600 block mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className={`input-flushed w-full ${errors.lastName ? 'border-red-500 bg-red-50' : ''}`}
                      required={!isLogin}
                      data-testid="last-name-input"
                    />
                    {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="text-xs tracking-widest uppercase text-leather-600 block mb-2">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`input-flushed w-full ${errors.email ? 'border-red-500 bg-red-50' : ''}`}
                required
                data-testid="email-input"
              />
              {errors.email && <p className="text-red-500 text-sm mt-1 flex items-center gap-1"><AlertCircle className="w-4 h-4" />{errors.email}</p>}
            </div>

            <div>
              <label className="text-xs tracking-widest uppercase text-leather-600 block mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`input-flushed w-full pr-10 ${errors.password ? 'border-red-500 bg-red-50' : ''}`}
                  required
                  minLength={6}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-leather-600 hover:text-leather-900"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password && <p className="text-red-500 text-sm mt-1 flex items-center gap-1"><AlertCircle className="w-4 h-4" />{errors.password}</p>}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              data-testid="submit-btn"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Processing...
                </span>
              ) : (
                isLogin ? 'Sign In' : 'Create Account'
              )}
            </button>
          </form>

          <p className="text-center mt-8 text-leather-600">
            {isLogin ? "Don't have an account?" : 'Already have an account?'}
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="ml-2 text-leather-900 underline underline-offset-4 hover:text-gold-600"
              data-testid="toggle-auth-btn"
            >
              {isLogin ? 'Sign up' : 'Sign in'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
